'use client'

import { useQuery } from '@tanstack/react-query'
import { getWaveformPreview } from '@/lib/assets/assets'
import type { AssetSummary } from '@/lib/assets/types'

async function fetchWaveformSamples(url: string, signal: AbortSignal): Promise<number[]> {
  const response = await fetch(url, { signal })
  if (!response.ok) throw new Error(`Waveform request failed: ${response.status}`)

  const data: unknown = await response.json()
  if (!Array.isArray(data)) throw new Error('Invalid waveform data')

  return data.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
}

export function useAudioWaveform(asset?: AssetSummary | null) {
  const url = asset ? getWaveformPreview(asset) : null
  return useQuery<number[] | null>({
    queryKey: ['waveform', url],
    queryFn: async ({ signal }) => {
      if (!url) return null
      let values: number[]
      try {
        values = await fetchWaveformSamples(url, signal)
      } catch (error) {
        if (signal.aborted) throw error
        values = await fetchWaveformSamples(`/api/assets/waveform?url=${encodeURIComponent(url)}`, signal)
      }

      const maximum = values.reduce((peak, value) => Math.max(peak, Math.abs(value)), 0)
      return maximum > 0 ? values.map((value) => (Math.abs(value) * 100) / maximum) : []
    },
    enabled: !!url,
    staleTime: Infinity,
    retry: 1,
  })
}
